import { startOfDay } from "date-fns";
import { parseUtcDate } from "@/lib/ui-helper/dates";
import {
  periodsConfigSchema,
  type PeriodsConfig,
} from "@/lib/ui-helper/periods.schema";
import type { ConversationMetadata } from "@/types";
import periodsConfigRaw from "@/constants/periods.json";

const EPOCH_MS = 0;

export const periodsConfig: PeriodsConfig =
  periodsConfigSchema.parse(periodsConfigRaw);

const FRENCH_MONTH_LABELS = [
  "Janvier",
  "Février",
  "Mars",
  "Avril",
  "Mai",
  "Juin",
  "Juillet",
  "Août",
  "Septembre",
  "Octobre",
  "Novembre",
  "Décembre",
] as const;

export interface PeriodTreeLeaf {
  id: string;
  label: string;
  kind: "leaf";
  conversations: ConversationMetadata[];
}

export interface PeriodTreeGroup {
  id: string;
  label: string;
  kind: "group";
  totalCount: number;
  children: PeriodTreeLeaf[];
}

export type PeriodTreeNode = PeriodTreeLeaf | PeriodTreeGroup;

export type FlatVirtualItem =
  | { type: "group-header"; groupId: string; label: string; count: number }
  | {
      type: "leaf-header";
      groupId: string | null;
      leafId: string;
      label: string;
      count: number;
    }
  | { type: "card"; conversation: ConversationMetadata };

export interface ConversationPeriodPlacement {
  groupId: string | null;
  leafId: string;
}

interface DateContext {
  activityYear: number;
  activityMonth: number;
}

type Classification =
  | { kind: "year"; year: number; month: number }
  | { kind: "older" };

function getConversationActivityDate(
  conv: ConversationMetadata
): Date | undefined {
  const dateStr =
    conv.lastActivityAt || conv.startedAt || conv.updatedAt || conv.createdAt;
  if (!dateStr) return undefined;

  const parsed = parseUtcDate(dateStr);
  if (parsed.getTime() === EPOCH_MS) return undefined;
  return parsed;
}

function buildDateContext(date: Date): DateContext {
  const activityDay = startOfDay(date);
  return {
    activityYear: activityDay.getFullYear(),
    activityMonth: activityDay.getMonth(),
  };
}

function classifyConversation(
  conv: ConversationMetadata,
  now: Date,
  config: PeriodsConfig
): Classification {
  const date = getConversationActivityDate(conv);
  if (!date) {
    return { kind: "older" };
  }

  const { activityYear, activityMonth } = buildDateContext(date);
  if (
    config.minYear != null &&
    Number.isFinite(config.minYear) &&
    activityYear < config.minYear
  ) {
    return { kind: "older" };
  }

  return { kind: "year", year: activityYear, month: activityMonth };
}

export function getCurrentCalendarYear(now: Date = new Date()): number {
  return now.getFullYear();
}

export function yearGroupId(year: number): string {
  return `year:${year}`;
}

function yearMonthLeafId(year: number, monthIndex: number): string {
  return `year:${year}-month:${monthIndex + 1}`;
}

function monthLabel(monthIndex: number): string {
  return FRENCH_MONTH_LABELS[monthIndex] ?? `Mois ${monthIndex + 1}`;
}

export function isCurrentYearGroupId(
  groupId: string,
  now: Date = new Date()
): boolean {
  return groupId === yearGroupId(getCurrentCalendarYear(now));
}

export function buildConversationPeriodTree(
  conversations: ConversationMetadata[],
  now: Date = new Date(),
  config: PeriodsConfig = periodsConfig
): PeriodTreeNode[] {
  const olderConfig = config.tree.find((n) => n.type === "older");

  const yearGroups = new Map<number, Map<number, ConversationMetadata[]>>();
  const olderConversations: ConversationMetadata[] = [];

  for (const conv of conversations) {
    const classification = classifyConversation(conv, now, config);
    if (classification.kind === "older") {
      olderConversations.push(conv);
      continue;
    }

    let months = yearGroups.get(classification.year);
    if (!months) {
      months = new Map();
      yearGroups.set(classification.year, months);
    }
    const monthBucket = months.get(classification.month) ?? [];
    monthBucket.push(conv);
    months.set(classification.month, monthBucket);
  }

  const result: PeriodTreeNode[] = [];

  const sortedYears = [...yearGroups.keys()].sort((a, b) => b - a);
  for (const year of sortedYears) {
    const monthsMap = yearGroups.get(year)!;
    const sortedMonths = [...monthsMap.keys()].sort((a, b) => b - a);
    const children: PeriodTreeLeaf[] = sortedMonths.map((monthIndex) => ({
      id: yearMonthLeafId(year, monthIndex),
      label: monthLabel(monthIndex),
      kind: "leaf",
      conversations: monthsMap.get(monthIndex) ?? [],
    }));

    const totalCount = children.reduce(
      (sum, leaf) => sum + leaf.conversations.length,
      0
    );
    if (totalCount > 0) {
      result.push({
        id: yearGroupId(year),
        label: String(year),
        kind: "group",
        totalCount,
        children,
      });
    }
  }

  if (olderConversations.length > 0 && olderConfig?.type === "older") {
    result.push({
      id: olderConfig.id,
      label: olderConfig.label,
      kind: "leaf",
      conversations: olderConversations,
    });
  }

  return result;
}

export function collectActiveCollapseIds(tree: PeriodTreeNode[]): {
  groups: string[];
  leaves: string[];
} {
  const groups: string[] = [];
  const leaves: string[] = [];

  for (const node of tree) {
    if (node.kind === "group") {
      groups.push(node.id);
      for (const child of node.children) {
        leaves.push(child.id);
      }
    } else {
      leaves.push(node.id);
    }
  }

  return { groups, leaves };
}

export function splitActiveCollapseIdsByCurrentYear(
  activeCollapseIds: { groups: string[]; leaves: string[] },
  now: Date = new Date()
): {
  pastYearGroupIds: string[];
  leafIds: string[];
} {
  const currentYearGroupId = yearGroupId(getCurrentCalendarYear(now));
  return {
    pastYearGroupIds: activeCollapseIds.groups.filter(
      (id) => id !== currentYearGroupId
    ),
    leafIds: activeCollapseIds.leaves,
  };
}

export function buildCollapseAllState(
  activeCollapseIds: { groups: string[]; leaves: string[] },
  existingGroups: ReadonlySet<string>,
  now: Date = new Date()
): { groups: string[]; leaves: string[] } {
  const { pastYearGroupIds, leafIds } = splitActiveCollapseIdsByCurrentYear(
    activeCollapseIds,
    now
  );
  const groups = new Set(existingGroups);
  for (const id of pastYearGroupIds) {
    groups.add(id);
  }
  return { groups: [...groups], leaves: [...leafIds] };
}

export function isCollapseAllActive(
  activeCollapseIds: { groups: string[]; leaves: string[] },
  collapsedGroups: ReadonlySet<string>,
  collapsedLeaves: ReadonlySet<string>,
  now: Date = new Date()
): boolean {
  const { pastYearGroupIds, leafIds } = splitActiveCollapseIdsByCurrentYear(
    activeCollapseIds,
    now
  );
  const pastYearsCollapsed =
    pastYearGroupIds.length === 0 ||
    pastYearGroupIds.every((id) => collapsedGroups.has(id));
  const leavesCollapsed =
    leafIds.length === 0 || leafIds.every((id) => collapsedLeaves.has(id));
  return pastYearsCollapsed && leavesCollapsed;
}

export function buildConversationPeriodIndex(
  tree: PeriodTreeNode[]
): Map<string, ConversationPeriodPlacement> {
  const index = new Map<string, ConversationPeriodPlacement>();

  for (const node of tree) {
    if (node.kind === "group") {
      for (const leaf of node.children) {
        for (const conv of leaf.conversations) {
          index.set(String(conv.id), {
            groupId: node.id,
            leafId: leaf.id,
          });
        }
      }
      continue;
    }

    for (const conv of node.conversations) {
      index.set(String(conv.id), {
        groupId: null,
        leafId: node.id,
      });
    }
  }

  return index;
}

export function shouldShowHiddenActiveConversationIndicator(params: {
  selectedConversationId: string | undefined;
  placement: ConversationPeriodPlacement | undefined;
  variant: "group" | "leaf";
  groupId: string | null;
  leafId: string | undefined;
  collapsedGroups: ReadonlySet<string>;
  collapsedLeaves: ReadonlySet<string>;
}): boolean {
  const {
    selectedConversationId,
    placement,
    variant,
    groupId,
    leafId,
    collapsedGroups,
    collapsedLeaves,
  } = params;

  if (!selectedConversationId || !placement) {
    return false;
  }

  if (variant === "group") {
    if (groupId == null || placement.groupId !== groupId) {
      return false;
    }
    return collapsedGroups.has(groupId);
  }

  if (leafId == null || placement.leafId !== leafId) {
    return false;
  }

  if (placement.groupId && collapsedGroups.has(placement.groupId)) {
    return false;
  }

  return collapsedLeaves.has(leafId);
}

/** Index of the active conversation card in the flattened virtual list, or -1. */
export function findConversationCardVirtualIndex(
  items: ReadonlyArray<FlatVirtualItem>,
  conversationId: string | undefined
): number {
  if (!conversationId) {
    return -1;
  }
  return items.findIndex(
    (item) =>
      item.type === "card" && String(item.conversation.id) === conversationId
  );
}

export function buildFlatVirtualItems(
  tree: PeriodTreeNode[],
  collapsedGroups: ReadonlySet<string>,
  collapsedLeaves: ReadonlySet<string>
): FlatVirtualItem[] {
  const items: FlatVirtualItem[] = [];

  for (const node of tree) {
    if (node.kind === "group") {
      items.push({
        type: "group-header",
        groupId: node.id,
        label: node.label,
        count: node.totalCount,
      });

      if (!collapsedGroups.has(node.id)) {
        for (const leaf of node.children) {
          items.push({
            type: "leaf-header",
            groupId: node.id,
            leafId: leaf.id,
            label: leaf.label,
            count: leaf.conversations.length,
          });

          if (!collapsedLeaves.has(leaf.id)) {
            for (const conv of leaf.conversations) {
              items.push({ type: "card", conversation: conv });
            }
          }
        }
      }
      continue;
    }

    items.push({
      type: "leaf-header",
      groupId: null,
      leafId: node.id,
      label: node.label,
      count: node.conversations.length,
    });

    if (!collapsedLeaves.has(node.id)) {
      for (const conv of node.conversations) {
        items.push({ type: "card", conversation: conv });
      }
    }
  }

  return items;
}
